import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Plus, 
  Clock, 
  Target, 
  BarChart3,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Loader2,
  XCircle,
  Sparkles,
  FileEdit,
  Wand2,
  Box,
} from "lucide-react";
import type { RunStatus, ModuleType } from "@/lib/types";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

const statusConfig: Record<RunStatus, { icon: React.ElementType; label: string; className: string }> = {
  draft: { icon: Clock, label: "Draft", className: "bg-muted text-muted-foreground" },
  queued: { icon: Clock, label: "Queued", className: "bg-muted text-muted-foreground" },
  running: { icon: Loader2, label: "Running", className: "bg-reqqy-warning/20 text-reqqy-warning" },
  succeeded: { icon: CheckCircle2, label: "Succeeded", className: "bg-reqqy-success/20 text-reqqy-success" },
  failed: { icon: XCircle, label: "Failed", className: "bg-destructive/20 text-destructive" },
  cancelled: { icon: AlertCircle, label: "Cancelled", className: "bg-muted text-muted-foreground" },
};

const moduleConfig: Record<ModuleType, { icon: React.ElementType; label: string }> = {
  generation: { icon: Sparkles, label: "Generate" },
  evaluation: { icon: FileEdit, label: "Evaluate" },
  refinement: { icon: Wand2, label: "Refine" },
  prototype: { icon: Box, label: "Prototype" },
};

export default function ProjectDetails() {
  const { projectId } = useParams<{ projectId: string }>();

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!projectId,
  });

  const { data: runs = [], isLoading: runsLoading } = useQuery({
    queryKey: ["project-runs", projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const { data, error } = await supabase
        .from("runs")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!projectId,
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getClarityColor = (score: number | undefined | null) => {
    if (!score) return "text-muted-foreground";
    if (score >= 80) return "text-reqqy-success";
    if (score >= 60) return "text-reqqy-warning";
    return "text-destructive";
  };

  const completedRuns = runs.filter(r => r.status === "succeeded" && r.clarity_score);
  const avgClarityScore = completedRuns.length > 0
    ? Math.round(completedRuns.reduce((sum, r) => sum + (Number(r.clarity_score) || 0), 0) / completedRuns.length)
    : null;

  if (projectLoading || runsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Project Not Found</h2>
          <p className="text-muted-foreground mb-4">The requested project could not be found.</p>
          <Button asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-display font-bold mb-1">
              {project.name}
            </h1>
            <p className="text-muted-foreground">{project.description}</p>
          </div>
          <Button asChild className="gradient-primary text-primary-foreground hover:opacity-90">
            <Link to="/dashboard/new-run">
              <Plus className="w-4 h-4 mr-2" />
              New Run
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Card className="glass">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold">{runs.length}</div>
              <div className="text-sm text-muted-foreground">Total Runs</div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-reqqy-success/10 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-reqqy-success" />
            </div>
            <div>
              <div className="text-2xl font-bold">{completedRuns.length}</div>
              <div className="text-sm text-muted-foreground">Succeeded</div>
            </div>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-accent" />
            </div>
            <div>
              <div className={cn("text-2xl font-bold", getClarityColor(avgClarityScore))}>
                {avgClarityScore ? `${avgClarityScore}%` : "—"}
              </div>
              <div className="text-sm text-muted-foreground">Avg. Clarity</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Runs List */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Run History</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No runs yet. Start a new run to get started!
            </div>
          ) : (
            <div className="space-y-3">
              {runs.map((run) => {
                const status = statusConfig[run.status as RunStatus] || statusConfig.draft;
                const moduleType = (run.module_type as ModuleType) || "generation";
                const moduleInfo = moduleConfig[moduleType];
                const StatusIcon = status.icon;
                const ModuleIcon = moduleInfo.icon;
                const inputPayload = run.input_payload as Record<string, unknown> | null;
                const rawInput = (inputPayload?.raw_input as string) || "Untitled run";
                const configPayload = run.config_payload as Record<string, unknown> | null;
                
                return (
                  <Link
                    key={run.id}
                    to={run.status === "succeeded" ? `/dashboard/runs/${run.id}/results` : `/dashboard/runs/${run.id}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-background/50 hover:bg-background transition-colors group"
                  >
                    <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", status.className)}>
                      <StatusIcon className={cn("w-5 h-5", run.status === "running" && "animate-spin")} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold truncate group-hover:text-primary transition-colors">
                          {rawInput.slice(0, 50)}{rawInput.length > 50 ? "..." : ""}
                        </h3>
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          <ModuleIcon className="w-3 h-3" />
                          {moduleInfo.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="capitalize">{(configPayload?.depth as string) || "quick"} analysis</span>
                      </div>
                    </div>

                    {run.clarity_score && (
                      <div className="hidden md:flex items-center gap-2">
                        <div className={cn("text-lg font-bold", getClarityColor(Number(run.clarity_score)))}>
                          {Number(run.clarity_score)}%
                        </div>
                        <span className="text-xs text-muted-foreground">clarity</span>
                      </div>
                    )}

                    <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      {formatDate(run.created_at)}
                    </div>

                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
